/**
 * Given an PrintPackage retrieve the PrintPackageItem related to
 * the given type, and optional parameters.
 *
 * @param {Object} options the options
 * @param {Object} [options.printPackage] the printPackage
 * @param {String} [options.typeId] the ID of the PrintPackageItemType related to the item
 * @param {String} [options.printProductSizeId] the ID of the PrintProductSize related to the item
 * @param {String} [options.customPrintId] the ID of the CustomPrint related to the item
 * @param {Boolean} [options.isOptional] whether the PrintPackageItem is optional or not
 * @returns {undefined|Object}
 */
export const getPrintPackageAddOnItem = ({
  printPackage,
  typeId,
  printProductSizeId,
  customPrintId,
  isOptional,
}) => {
  if (!printPackage?.items?.records?.length) {
    return undefined;
  }

  return printPackage.items.records.find(
    item =>
      ((printProductSizeId &&
        item.print_product_configuration?.print_product_size?.id ===
          printProductSizeId) ||
        (typeId && item.type?.id === typeId) ||
        (customPrintId && item.custom_print?.id === customPrintId)) &&
      item.is_optional === isOptional &&
      item.quantity === 1,
  );
};

/**
 * Given an AlbumFolderPrintPackage retrieve the AlbumFolderPrintPackageItem
 * related to the given tag, type, and optional parameters.
 *
 * @param {Object} options the options
 * @param {Object} [options.albumFolderPrintPackage] the albumFolderPrintPackage
 * @param {String} [options.tagId] the ID of the albumFolderTag related to the item
 * @param {String} [options.typeId] the ID of the PrintPackageItemType related to the item
 * @param {String} [options.printProductSizeId] the ID of the PrintProductSizeId related to the item
 * @param {String} [options.customPrintId] the ID of the CustomPrint related to the item
 * @param {Boolean} [options.isOptional] whether the PrintPackageItem is optional or not
 * @returns {undefined|Object}
 */
export const getAlbumFolderPrintPackageAddOnItem = ({
  albumFolderPrintPackage,
  tagId,
  typeId,
  printProductSizeId,
  customPrintId,
  isOptional,
}) => {
  if (!albumFolderPrintPackage?.items?.records?.length) {
    return undefined;
  }

  return albumFolderPrintPackage.items.records.find(
    item =>
      item.album_folder_tag.id === tagId &&
      ((printProductSizeId &&
        item.print_package_item.print_product_configuration?.print_product_size
          ?.id === printProductSizeId) ||
        (typeId && item.print_package_item.type?.id === typeId) ||
        (customPrintId && item.custom_print?.id === customPrintId)) &&
      item.print_package_item.is_optional === isOptional &&
      item.print_package_item.quantity === 1,
  );
};

/**
 * Given a printProduct, retrieve the valid print product sizes for packages & custom pricing
 *
 * @param {Object} printProduct the print product
 * @returns {Array} the valid print product sizes
 */
export const getPrintProductSizesForPackagesAndPricing = printProduct => {
  if (!printProduct || !printProduct?.sizes?.records) {
    return [];
  }

  return (printProduct?.sizes?.records ?? []).filter(
    ({ print_size: size }) => !size?.name?.includes('Template'),
  );
};
